import React, { useState } from 'react'
import { Kpis } from '../core/types'
import { Metric, METRICS, metricState, readMetricMode } from '../core/metrics'
import { METRIC_MODE_STORAGE_PREFIX } from '../core/consts'
import { KTIcon } from '../../../../../../_metronic/helpers'
import { METRIC_HINTS } from '../core/hints'
import InfoHint from '../../../../../common/InfoHint'

type Props = {
  kpis: Kpis
  onOpenDrilldown: (
    signal: string,
    title: string,
    filters?: { currency?: string; merchant?: number }
  ) => void
}

// Subtle underlined "details" link: dark text, brightens on hover, shifts on press.
const DetailsLink: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [pressed, setPressed] = useState(false)
  return (
    <button
      type='button'
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      className='btn btn-link btn-sm p-0 mt-2 text-gray-700 text-hover-primary text-decoration-underline fs-8'
      style={{ transform: pressed ? 'translateY(1px)' : 'none' }}
    >
      Подробнее
    </button>
  )
}

// Выбранный режим карточки живёт в браузере, как выбранная валюта. Хранилище может
// быть недоступно (приватный режим, запрет сайта) — тогда режим по умолчанию.
const useMetricMode = (m: Metric): [string | undefined, (mode: string) => void] => {
  const key = METRIC_MODE_STORAGE_PREFIX + m.key
  const [mode, setMode] = useState(() => {
    try {
      return readMetricMode(m, localStorage.getItem(key))
    } catch {
      return readMetricMode(m, null)
    }
  })
  const choose = (next: string) => {
    setMode(next)
    try {
      localStorage.setItem(key, next)
    } catch {
      // режим останется до перезагрузки страницы
    }
  }
  return [mode, choose]
}

const KpiCard: React.FC<Props & { m: Metric }> = ({ m, kpis, onOpenDrilldown }) => {
  const [mode, setMode] = useMetricMode(m)
  const color = metricState(m, kpis)
  const sub = m.sub?.(kpis, mode)
  const trend = m.trend?.(kpis, mode) ?? null
  return (
    <div className='col-12 col-sm-6 col-xl-3'>
      <div className='card h-100 border border-gray-300'>
        <div className='card-body p-4 d-flex flex-column'>
          <div className='text-gray-700 fs-7 fw-semibold mb-2 d-flex align-items-start'>
            <span className='d-flex align-items-center'>
              {m.title}
              <InfoHint text={METRIC_HINTS[m.hintKey]} />
            </span>
            {/* Компактный сегментный переключатель в строке заголовка: режим уточняет
                заголовок, а не спорит с числом за внимание. */}
            {m.modes && (
              <div
                className='d-inline-flex flex-shrink-0 ms-auto ps-2 bg-light rounded p-1'
                role='group'
                aria-label='Режим карточки'
              >
                {m.modes.map((x) => (
                  <button
                    key={x.key}
                    type='button'
                    title={x.label}
                    aria-pressed={mode === x.key}
                    className={`btn btn-sm border-0 rounded px-2 py-0 fs-9 lh-lg ${
                      mode === x.key
                        ? 'bg-body shadow-sm text-gray-900 fw-semibold'
                        : 'bg-transparent text-gray-600 text-hover-gray-900 fw-normal'
                    }`}
                    onClick={() => setMode(x.key)}
                  >
                    {x.short}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className='d-flex align-items-center'>
            <span className={`badge badge-light-${color} text-${color} fs-2 fw-bold px-3 py-2`}>
              {m.value(kpis, mode)}
            </span>
            {/* Стрелка приглушена намеренно: у показателя без нормы направление —
                    факт, а не оценка, и красить его в «хорошо/плохо» нечем. */}
            {trend && (
              <span
                className='d-inline-flex align-items-center text-gray-600 fs-8 ms-2'
                title='Против предыдущих 24 часов'
              >
                <KTIcon
                  iconName={trend.direction === 'up' ? 'arrow-up-right' : 'arrow-down-right'}
                  className='fs-7'
                />
                <span className='ms-1'>
                  {trend.delta > 0 ? '+' : '−'}
                  {Math.abs(trend.delta).toFixed(2)}
                </span>
              </span>
            )}
          </div>
          {m.subLines ? (
            <div className='text-gray-700 fs-8 mt-2'>
              {m.subLines(kpis, mode).map((line) => (
                <div key={line.text} className={line.indent ? 'ps-3 text-gray-600' : undefined}>
                  {line.text}
                </div>
              ))}
            </div>
          ) : (
            sub && <div className='text-gray-700 fs-8 mt-2'>{sub}</div>
          )}
          {m.signal && (
            <div className='mt-auto pt-2'>
              <DetailsLink
                onClick={() =>
                  onOpenDrilldown(m.signal as string, m.title, { currency: kpis.currency })
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const KpiBadges: React.FC<Props> = ({ kpis, onOpenDrilldown }) => (
  // 4×2 grid: neutral cards, state shown only as a colored chip on the value
  // (no full-card "traffic light"). Secondary text darkened for contrast.
  <div className='row g-4 mb-5'>
    {METRICS.map((m) => (
      <KpiCard key={m.key} m={m} kpis={kpis} onOpenDrilldown={onOpenDrilldown} />
    ))}
  </div>
)

export default KpiBadges
